import type { ReviewFileDto, ReviewSetDto, ReviewerDto } from '@nsb/core';
import { dateWords } from '../../days.ts';

/**
 * An iteration and the step of the process that delivers it (T1 B, B1 A). A review starts on such a step and is
 * shared only while that step is on the job, the rule the API's share keeps (`shareSet`, `no_step`): on it; on none
 * (made by hand before reviews came only from steps, or its step gone); or on one turned off, where finishing the
 * review marks nothing done.
 */
export type ReviewStep = 'on' | 'none' | 'off';

type Iteration = Pick<ReviewSetDto, 'task' | 'stepGone' | 'sharedAt'>;

export function reviewStep(s: Iteration): ReviewStep {
  return s.stepGone ?? (s.task ? 'on' : 'none');
}

/** Only an iteration on a step the job has is shared. */
export const canShare = (s: Iteration): boolean => !s.sharedAt && reviewStep(s) === 'on';

/** What NSB reads under an iteration about its step, or null when there is nothing to say. */
export function reviewStepLine(s: Iteration): string | null {
  const step = reviewStep(s);
  if (step === 'on') return null;
  if (!s.sharedAt) {
    return step === 'off'
      ? 'Its step is turned off on the process. Turn it on again to share this.'
      : 'It is on no step of the process, so it cannot be shared yet. The next step added that delivers a design review takes it, the oldest such draft first.';
  }
  if (step === 'off') return 'Its step is turned off on the process, so finishing the review marks nothing done.';
  // Shared before reviews came only from steps: there is no step for it to finish.
  return null;
}

/**
 * Over NSB's note on a shared review (P-24, design study 27): whose it is, by their first name, and the day it went
 * with the email; null for a draft (NSB's own words still) or a review shared with no note.
 */
export function shareNoteLabel(s: Pick<ReviewSetDto, 'note' | 'sharedAt' | 'sharedBy'>): string | null {
  if (!s.note || !s.sharedAt) return null;
  const who = s.sharedBy?.trim().split(/\s+/)[0] || 'NSB';
  return `${who}’s note · ${dateWords(s.sharedAt, 'dayOrDate')}`;
}

type Placed = Pick<ReviewSetDto, 'id' | 'phase' | 'reached' | 'sharedAt' | 'task'>;

/** One design review, the step that delivers it: its newest iteration, and those before it, newest first (P-22). */
export interface ReviewGroup<S> {
  newest: S;
  earlier: S[];
}

/**
 * The reviews phase by phase, in the process's order as the API gives them, each review's iterations newest first
 * (P-22, design study 27): a review the work has reached, shared or with an earlier iteration, open, its newest in full
 * (show A); one whose step the work has not reached yet named under Later in the process, never opened (reached A).
 */
export function reviewPhases<S extends Placed>(sets: readonly S[]): { phase: string; open: ReviewGroup<S>[]; later: S[] }[] {
  const phases = new Map<string, { phase: string; reviews: ReviewGroup<S>[] }>();
  const reviews = new Map<string, ReviewGroup<S>>();
  for (const s of sets) {
    const k = s.phase.toLowerCase();
    if (!phases.has(k)) phases.set(k, { phase: s.phase, reviews: [] });
    const key = s.task?.id ?? s.id;
    const seen = reviews.get(key);
    if (seen) {
      seen.earlier.push(s);
      continue;
    }
    const review = { newest: s, earlier: [] as S[] };
    reviews.set(key, review);
    phases.get(k)!.reviews.push(review);
  }
  const opened = (r: ReviewGroup<S>) => r.newest.reached || r.newest.sharedAt !== null || r.earlier.length > 0;
  return [...phases.values()].map(({ phase, reviews: rs }) => ({ phase, open: rs.filter(opened), later: rs.filter((r) => !opened(r)).map((r) => r.newest) }));
}

/**
 * An earlier iteration of a review, as one line under the newest (show A): when it was shared, who reviewed it or who
 * it waits on, and how much was said on it.
 */
export function earlierLine(s: Pick<ReviewSetDto, 'sharedAt' | 'reviewed' | 'reviewers' | 'comments' | 'files'>): string {
  const names = s.reviewers.map((r) => r.name);
  const waiting = s.reviewers.filter((r) => !r.doneAt).map((r) => r.name);
  const who = names.length ? (s.reviewed ? `reviewed by ${names.join(' and ')}` : `waiting on ${waiting.join(' and ')}`) : null;
  const said = s.comments.length + s.files.reduce((n, f) => n + f.threads, 0);
  return [s.sharedAt ? `Shared ${dateWords(s.sharedAt, 'dayOrDate')}` : 'Not shared', who, said ? `${said} comment${said === 1 ? '' : 's'}` : 'no comments'].filter(Boolean).join(' · ');
}

// --- Sheets added to a shared iteration (design study 29) -------------------------------------------------------------

type Sheet = Pick<ReviewFileDto, 'id' | 'name' | 'unsent' | 'revises' | 'revisedBy' | 'round'>;

/**
 * An iteration's sheets as its page lays them out (design study 29): those as they stand, then those revised since,
 * below them (earlier B: "i think it can below the later sheets"), then those not sent yet, NSB's alone. The API gives
 * them in reading order (core `inSheetOrder`), a revision where the sheet it revises was.
 */
export function sheetGroups<F extends Sheet>(files: readonly F[]): { standing: F[]; revised: F[]; unsent: F[] } {
  return {
    standing: files.filter((f) => !f.unsent && !f.revisedBy),
    revised: files.filter((f) => !f.unsent && f.revisedBy !== null),
    unsent: files.filter((f) => f.unsent),
  };
}

/** The day a sheet was revised: the day the sheet revising it went to the reviewers, when the page has it. */
export function revisedOn(f: Sheet, files: readonly Sheet[]): string | null {
  return (f.revisedBy && files.find((x) => x.id === f.revisedBy!.id)?.round?.at) || null;
}

const firstOf = (name: string | null | undefined) => name?.trim().split(/\s+/)[0] || 'NSB';

/**
 * NSB's notes with sheets sent after the iteration was shared, oldest first, each labelled as the share's note is
 * (`shareNoteLabel`): whose, the day it went, and what went with it.
 */
export function roundNotes(files: readonly Sheet[]): { id: string; label: string; note: string }[] {
  const rounds = new Map<string, { at: string; by: string; note: string; names: string[] }>();
  for (const f of files) {
    if (!f.round?.note) continue;
    const r = rounds.get(f.round.id) ?? { at: f.round.at, by: f.round.by, note: f.round.note, names: [] };
    r.names.push(f.name);
    rounds.set(f.round.id, r);
  }
  return [...rounds.entries()]
    .sort(([, a], [, b]) => a.at.localeCompare(b.at))
    .map(([id, r]) => ({
      id,
      note: r.note,
      label: `${firstOf(r.by)}’s note · ${dateWords(r.at, 'dayOrDate')} · with ${r.names.length === 1 ? r.names[0] : `${r.names.length} sheets`}`,
    }));
}

/** Who a round goes to, by name: one or two named, more counted. */
function whoIsTold(reviewers: readonly Pick<ReviewerDto, 'name'>[]): string {
  const names = reviewers.map((r) => r.name);
  return names.length === 0 ? 'nobody yet' : names.length <= 2 ? names.join(' and ') : `the ${names.length} people asked`;
}

/** What the drop zone under a shared iteration says a sheet put there does (design study 29). */
export function heldHint(reviewers: readonly Pick<ReviewerDto, 'name'>[]): string {
  return `Put here, a sheet waits for you to send it to ${whoIsTold(reviewers)}, with a note.`;
}

/** The button that sends the sheets not sent yet. */
export function sendWords(reviewers: readonly Pick<ReviewerDto, 'name'>[], count: number): string {
  return `Send ${count === 1 ? 'it' : 'them'} to ${whoIsTold(reviewers)}`;
}

/** What sending does, said before it is pressed: asked again (asked A), and opened again when it was reviewed (reviewed A). */
export function sendLine(s: Pick<ReviewSetDto, 'reviewed'>): string {
  return s.reviewed
    ? 'Everyone asked has reviewed it, so sending opens it again for comments, the sheets being why. Each of them is asked again.'
    : 'Each person asked is told, and anyone who had said they were done is asked again.';
}

/** A reviewer asked again by a new round, in words beside their name. */
export function askedAgainWords(r: Pick<ReviewerDto, 'doneAt' | 'askedAgain'>): string | null {
  return !r.doneAt && r.askedAgain ? `asked again ${dateWords(r.askedAgain, 'dayOrDate')}` : null;
}

/** What is new in a review a client is asked about again (design study 29): the sheets by name, or how many. */
export function addedWords(added: readonly string[] | null | undefined): string | null {
  if (!added?.length) return null;
  return `New: ${added.length <= 2 ? added.join(' and ') : `${added.length} sheets`}`;
}
